import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { type ReactNode, useState } from "react";
import { z } from "zod";
import { errorMessage } from "../../api/client";
import { ops, pdialCarrierQuery, qk } from "../../api/ops";
import type { OpsPdialCarrier, OpsPdialCarrierPatch, TelnyxTier } from "../../api/types";
import { CompanySelect, useCompanyChoice } from "../../components/CompanyPicker";
import { toast } from "../../components/toast";
import { Button, Card, ErrorState, HelpTip, Input, Loading, PageHeader, Select } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { useNavigate } from "../../lib/navigate";

const searchSchema = z.object({
  company: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/parallel-dialing")({
  staticData: { crumb: { label: "Parallel dialing", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: ParallelDialingPage,
});

const TIERS: { value: TelnyxTier; label: string }[] = [
  { value: "unknown", label: "Not confirmed" },
  { value: "trial", label: "Trial" },
  { value: "paid", label: "Paid" },
  { value: "verified", label: "Verified" },
  { value: "enterprise", label: "Enterprise" },
];

const STAGE: Record<string, string> = {
  delivery: "Telnyx answer to webhook received",
  decide: "Received to winner decided",
  send: "Decided to cancel sent",
  ack: "Cancel sent to Telnyx reply",
  server: "Received to cancel sent (our part)",
  answer_to_cancel: "Answer to loser hung up",
};

function ParallelDialingPage() {
  const { company } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const pick = (id: string) => navigate({ search: (p) => ({ ...p, company: id }), replace: true });
  const companies = useCompanyChoice(company, pick);
  const q = useQuery({ ...pdialCarrierQuery(company ?? ""), enabled: !!company });

  return (
    <>
      <PageHeader
        title="Parallel dialing"
        actions={<CompanySelect value={company} companies={companies.data?.items ?? []} onChange={pick} />}
      />
      {companies.isError ? (
        <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
      ) : !company || q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <Carrier key={company} company={company} s={q.data} />
      )}
    </>
  );
}

function useSave(company: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: OpsPdialCarrierPatch) => ops.putPdialCarrier(company, p),
    onSuccess: (v) => {
      qc.setQueryData(qk.pdialCarrier(company), v);
      toast("Saved");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
}

function InlineError({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="mt-1 text-caption text-danger-ink">
      {text}
    </p>
  );
}

function Row({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <li className="grid items-start gap-x-4 gap-y-2 px-4 py-3 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
      <div className="flex items-center gap-1.5 pt-1">
        <span className="font-medium text-ui">{label}</span>
        {help ? <HelpTip text={help} /> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

function Carrier({ company, s }: { company: string; s: OpsPdialCarrier }) {
  const c = s.capacity;
  const notes: string[] = [];
  if (c.tier !== "verified" && c.tier !== "enterprise") {
    const tier = TIERS.find((t) => t.value === c.tier)?.label ?? c.tier;
    notes.push(`Telnyx tier: ${tier}. Parallel dialing needs Verified or Enterprise.`);
  }
  if (c.refusals_7d > 0)
    notes.push(`Telnyx refused ${c.refusals_7d} parallel dials for capacity in the last 7 days.`);
  if (c.error) notes.push(`Last check failed: ${c.error}`);
  return (
    <div className="flex max-w-[880px] flex-col gap-4">
      {notes.length ? (
        <div className="flex flex-col gap-2">
          {notes.map((n) => (
            <div key={n} role="status" className="rounded-md bg-warning-soft px-3 py-2 text-body text-ink">
              {n}
            </div>
          ))}
        </div>
      ) : null}
      <CapacityCard key={`cap-${c.checked_at}-${JSON.stringify(c)}`} company={company} s={s} />
      <OffsetsCard key={`off-${s.start_offset_variants.join(",")}`} company={company} s={s} />
      <Card title="Cancel speed (7 days)">
        <Table aria-label="Cancel speed">
          <THead>
            <tr>
              <Th>Stage</Th>
              <Th numeric>p50</Th>
              <Th numeric>p95</Th>
              <Th numeric>Samples</Th>
            </tr>
          </THead>
          <TBody>
            {s.latency.map((l) => (
              <Tr key={l.stage}>
                <Td>{STAGE[l.stage] ?? l.stage}</Td>
                <Td numeric className="tnum">
                  {l.p50_ms === null ? "None" : `${Math.round(l.p50_ms)} ms`}
                </Td>
                <Td numeric className="tnum">
                  {l.p95_ms === null ? "None" : `${Math.round(l.p95_ms)} ms`}
                </Td>
                <Td numeric className="tnum">
                  {l.samples.toLocaleString()}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </Card>
      <Card title="Start-offset experiment">
        {s.experiment.length === 0 ? (
          <p className="px-4 py-3 text-body text-ink-2">No parallel batches in the last 30 days.</p>
        ) : (
          <Table aria-label="Start-offset experiment">
            <THead>
              <tr>
                <Th numeric>Offset</Th>
                <Th numeric>Answered batches</Th>
                <Th numeric>Second answers</Th>
                <Th numeric>Rate (95% interval)</Th>
                <Th numeric>Connects per hour</Th>
                <Th numeric>Median to connect</Th>
              </tr>
            </THead>
            <TBody>
              {s.experiment.map((v) => (
                <Tr key={v.offset_ms}>
                  <Td numeric className="tnum">
                    {v.offset_ms} ms
                  </Td>
                  <Td numeric className="tnum">
                    {v.answered_batches.toLocaleString()}
                  </Td>
                  <Td numeric className="tnum">
                    {v.collisions}
                  </Td>
                  <Td numeric className="tnum">
                    {pct(v.collision_rate)} ({pct(v.collision_low)} to {pct(v.collision_high)})
                  </Td>
                  <Td numeric className="tnum">
                    {v.connects_per_hour === null ? "None" : v.connects_per_hour.toFixed(1)}
                  </Td>
                  <Td numeric className="tnum">
                    {v.median_time_to_connect_ms === null
                      ? "None"
                      : `${(v.median_time_to_connect_ms / 1000).toFixed(1)} s`}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
        <p className="px-4 py-3 text-caption text-ink-3 tnum">
          Telling a 0.5% from a 0.4% rate takes about {s.experiment_sample_size.toLocaleString()} answered
          batches per offset.
        </p>
      </Card>
    </div>
  );
}

function pct(x: number, digits = 2): string {
  return `${(x * 100).toFixed(digits)}%`;
}

/** A typed cap: `null` when blank, `undefined` when not a whole number. */
function parseCap(raw: string): number | null | undefined {
  const t = raw.trim().replace(/,/g, "");
  if (t === "") return null;
  return /^\d+$/.test(t) ? Number(t) : undefined;
}

/** Offsets as typed, "0, 250, 500": the list, or `null` when any entry is not whole milliseconds. */
function parseOffsets(raw: string): number[] | null {
  const parts = raw
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  if (!parts.every((p) => /^\d+$/.test(p))) return null;
  return parts.map(Number);
}

type CapKey = "confirmed_concurrency" | "calls_per_day" | "calls_per_hour";

function CapacityCard({ company, s }: { company: string; s: OpsPdialCarrier }) {
  const qc = useQueryClient();
  const c = s.capacity;
  const save = useSave(company);
  const check = useMutation({
    mutationFn: () => ops.checkPdialCarrier(company),
    onSuccess: (v) => qc.setQueryData(qk.pdialCarrier(company), v),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Card
      title="Phone account capacity"
      actions={
        <Button size="sm" onClick={() => check.mutate()} busy={check.isPending}>
          Check now
        </Button>
      }
    >
      <ul className="divide-y divide-hairline">
        <Row label="Calls at once">
          <span className="text-body tnum">
            {c.concurrency === null ? "No limit" : c.concurrency}{" "}
            <span className="text-ink-3">
              ({c.source.replace(/_/g, " ")}
              {c.checked_at ? `, checked ${fmtDateTime(c.checked_at)}` : ""})
            </span>
          </span>
          <p className="mt-1 text-caption text-ink-3 tnum">
            {c.needed_now} needed now, {c.needed_for_all} for every granted rep at {s.max_lines} lines.
          </p>
        </Row>
        <Row
          label="Telnyx tier"
          help="As the Telnyx portal shows it. No Telnyx API reports it. Paid adds an automated-call notice and caps dials."
        >
          <Select
            aria-label="Telnyx tier"
            className="w-48"
            value={c.tier}
            disabled={save.isPending}
            onValueChange={(v) => v !== c.tier && save.mutate({ telnyx_tier: v as TelnyxTier })}
            options={TIERS}
          />
          <InlineError
            text={save.isError && save.variables?.telnyx_tier !== undefined ? errorMessage(save.error) : null}
          />
        </Row>
        <CapRow
          label="Concurrency cap"
          help="Only for an account with a concurrent call limit. Blank: not limited."
          field="confirmed_concurrency"
          initial={c.source.includes("confirmed") ? c.concurrency : null}
          company={company}
        />
        <CapRow
          label="Calls per day"
          field="calls_per_day"
          initial={c.calls_per_day}
          used={`${c.calls_today.toLocaleString()} today`}
          company={company}
        />
        <CapRow
          label="Calls per hour"
          field="calls_per_hour"
          initial={c.calls_per_hour}
          used={`${c.calls_this_hour.toLocaleString()} this hour`}
          company={company}
        />
      </ul>
    </Card>
  );
}

function CapRow({
  label,
  help,
  field,
  initial,
  used,
  company,
}: {
  label: string;
  help?: string;
  field: CapKey;
  initial: number | null;
  used?: string;
  company: string;
}) {
  const save = useSave(company);
  const [v, setV] = useState(initial === null ? "" : String(initial));
  const [invalid, setInvalid] = useState<string | null>(null);
  const submit = () => {
    const n = parseCap(v);
    if (n === undefined) return setInvalid("Enter a whole number, or leave it blank for no cap.");
    setInvalid(null);
    save.mutate({ [field]: n });
  };
  return (
    <Row label={label} {...(help ? { help } : {})}>
      <div className="flex items-center gap-2">
        <Input
          aria-label={label}
          placeholder="No limit"
          inputMode="numeric"
          className="w-24 tnum"
          value={v}
          aria-invalid={invalid ? true : undefined}
          onChange={(e) => setV(e.target.value)}
        />
        <Button size="sm" aria-label={`Save ${label.toLowerCase()}`} busy={save.isPending} onClick={submit}>
          Save
        </Button>
        {used ? <span className="text-caption text-ink-3 tnum">{used}</span> : null}
      </div>
      <InlineError text={invalid ?? (save.isError ? errorMessage(save.error) : null)} />
    </Row>
  );
}

function OffsetsCard({ company, s }: { company: string; s: OpsPdialCarrier }) {
  const save = useSave(company);
  const [v, setV] = useState(s.start_offset_variants.join(", "));
  const [invalid, setInvalid] = useState<string | null>(null);
  const list = parseOffsets(v);
  const dirty = list === null || list.join(",") !== s.start_offset_variants.join(",");
  const submit = () => {
    if (list === null) return setInvalid("Whole milliseconds, separated by commas.");
    setInvalid(null);
    save.mutate({ start_offset_variants: list });
  };
  return (
    <Card title="Start offsets">
      <ul>
        <Row
          label="Offsets"
          help="Milliseconds between the lines of a batch; each batch gets one at random. 0 alone runs no experiment."
        >
          <div className="flex items-center gap-2">
            <Input
              aria-label="Start offsets"
              className="w-48 tnum"
              value={v}
              aria-invalid={invalid ? true : undefined}
              onChange={(e) => setV(e.target.value)}
            />
            <Button
              size="sm"
              aria-label="Save start offsets"
              disabled={!dirty}
              busy={save.isPending}
              onClick={submit}
            >
              Save
            </Button>
          </div>
          <InlineError text={invalid ?? (save.isError ? errorMessage(save.error) : null)} />
        </Row>
      </ul>
    </Card>
  );
}
